import React, { useEffect, useMemo, useRef, useState } from "react";
import { wormColors } from "core/src/core/constants.js";
import CoreGameFactory from "core/src/game-factory.js";
import * as gsf from "core/src/core/game-state-functions.js";

import GameCanvas from "../../../game-canvas/GameCanvas";
import LocalGameHandler from "../../../shared/local-game-handler.js";
import { equippedHeadStyle, equippedVictoryAnim } from "../../../shared/local-players";
import { useEggBalance } from "../../../shared/shop/useEggBalance";
import { eggIconUrl } from "../../../shared/golden-eggs";
import Equipment from "../../shop/Equipment";
import { createMap } from "../lobby";
import type { useLocalRoster } from "./hooks";
import { Swatches } from "./parts";
import { ChevL, Person } from "./icons";
import s from "./start.module.scss";
import { ON_PORTAL } from "../../../shared/portal-env";

const coreGameFactory = CoreGameFactory();

// The page's game: your worm alone on a small arena the shape of the screen
// (so its walls run along the phone's edges and the worm reads large), no
// power-ups, played for real by a bot, at an easy pace. It drives until it
// crashes; only then, after a pause to see it, the picture fades out, a new
// worm is set up unseen, and it fades back in. While it drives it is in turn
// five seconds as in a round and five as a victory lap, so the celebration you
// wear plays at its head (the lap is switched off again by taking its event
// back out of the game: core has no way out of a lap, a match never needs one).
const ARENA_H = 210;      // the arena's height in game units: small, so the worm is big
const SPEED = 62;         // game units a second (a match runs at 100)
const PLAIN_MS = 5000;
const LAP_MS = 5000;
const CRASH_PAUSE_MS = 1600;
const FADE_MS = 450;      // (the stylesheet's .plrArena transition)
export function WormStage({ player, ratio, onFade }: { player: { name: string; colorId: string; flag: string | null; headStyle: string | null; victoryAnim: string | null }; ratio: number; onFade: (hidden: boolean) => void }) {
  const [run, setRun] = useState<{ handler: any; n: number } | null>(null);
  const runs = useRef(0);
  const fadeRef = useRef(onFade);
  fadeRef.current = onFade;
  // What the worm wears can change while it drives: the run carries on, and only
  // the picture is redrawn (the canvas is rebuilt from the same game), so a new
  // colour or head shows on the worm that is already out there.
  const look = `${player.colorId}|${player.headStyle}|${player.victoryAnim}|${player.flag}`;
  const playerRef = useRef(player);
  playerRef.current = player;
  const seat = (p: typeof player) => [{ type: "bot", id: "me", name: p.name, colorId: p.colorId, left: null, right: null, flag: p.flag, headStyle: p.headStyle, victoryAnim: p.victoryAnim }];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const players = useMemo(() => seat(player), [look]);
  const sig = String(Math.round(ratio * 50));
  useEffect(() => {
    let handler: any = null, timer = 0, swap = 0, live = true;
    const start = () => {
      if (!live) return;
      let game: any;
      try {
        // A map's own width and height include its wall on every side, so the
        // play area is sized for the whole of it to have the stage's shape
        // (else the bottom wall falls outside the picture).
        const probe = createMap(`Rectangle ${ARENA_H * 2} ${ARENA_H}`);
        const wall = Math.max(0, (probe.height - ARENA_H) / 2);
        const map = createMap(`Rectangle ${Math.round(ratio * (ARENA_H + 2 * wall) - 2 * wall)} ${ARENA_H}`);
        // (no countdown and no roll call: it is a picture of you playing, not a match)
        game = coreGameFactory.create({ seed: Math.floor(Math.random() * 100000), map, players: seat(playerRef.current), powerUpSpawnChance: 0, wormSpeed: SPEED, startPhaseDuration: 0.05, rollCallDuration: 0 });
      } catch { return; } // decorative: no arena, no worm
      handler?.stop();
      handler = LocalGameHandler({ game, onGameOver: () => {}, maxFps: 60 });
      handler.start();
      setRun({ handler, n: ++runs.current });
      fadeRef.current(false);
      let lap = false, turnAt = Date.now() + PLAIN_MS, crashedAt = 0, leaving = false;
      window.clearInterval(timer);
      timer = window.setInterval(() => {
        const gs = game.gameState, now = Date.now();
        if (leaving) return;
        if (!crashedAt && gsf.getAlivePlayers(gs).length === 0) crashedAt = now;
        if (crashedAt) {
          if (now - crashedAt < CRASH_PAUSE_MS) return;
          // out of sight first, then the new worm
          leaving = true;
          fadeRef.current(true);
          swap = window.setTimeout(start, FADE_MS + 60);
          return;
        }
        if (now < turnAt || gsf.isInStartPhase(gs)) return;
        if (!lap) {
          if (playerRef.current.victoryAnim) { game.enterVictoryLap(); lap = true; }
          turnAt = now + LAP_MS;
        } else {
          gs.victoryLap = false;
          for (let k = gs.gameEvents.length - 1; k >= 0; k--) if (gs.gameEvents[k].type === "victory_lap") gs.gameEvents.splice(k, 1);
          lap = false;
          turnAt = now + PLAIN_MS;
        }
      }, 100);
    };
    start();
    return () => { live = false; window.clearInterval(timer); window.clearTimeout(swap); handler?.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  // A new look is drawn UNDER the picture that is up, which is taken away a
  // moment later: the swap shows a finished picture, never an empty arena.
  const [layers, setLayers] = useState<{ key: string; players: any[] }[]>([]);
  const layerKey = run ? run.n + ":" + look : "";
  useEffect(() => {
    if (!run) return;
    setLayers((cur) => {
      const sameRun = cur.filter((l) => l.key.startsWith(run.n + ":") && l.key !== layerKey);
      return [...sameRun.slice(-1), { key: layerKey, players }];
    });
    const id = window.setTimeout(() => setLayers((cur) => cur.filter((l) => l.key === layerKey)), 260);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerKey]);
  if (!run) return null;
  return (
    <>
      {layers.map((l, i) => (
        <div key={l.key} className={s.plrLayer} style={{ zIndex: layers.length - i }}>
          <GameCanvas config={{ preview: true }} gameState={run.handler.gameState} players={l.players as any} />
        </div>
      ))}
    </>
  );
}

export default function PlayerScreen({ roster, signedIn, instant, onClose, onProfile, onShop, onLogin, onSignOut }: {
  roster: ReturnType<typeof useLocalRoster>;
  signedIn: boolean;
  // back from the shop or your profile: it is there at once, no fade in over the home screen
  instant?: boolean;
  onClose: () => void;
  onProfile: () => void;
  onShop?: (tab?: string) => void; // (none on a game site's build: no Store there)
  onLogin: () => void;
  onSignOut: () => void;
}) {
  const me = roster.players[0];
  const eggs = useEggBalance();
  const [colours, setColours] = useState(false);
  const head = equippedHeadStyle(), victory = equippedVictoryAnim();
  // The arena takes the shape of the room it has (the screen, inside its safe
  // areas), so all four walls show; measured, since a phone browser's own bars
  // make the window's numbers unreliable.
  const stageRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState<number | null>(null);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const read = () => {
      const w = el.clientWidth, h = el.clientHeight;
      if (w > 0 && h > 0) setRatio((cur) => { const r = Math.max(1.4, Math.min(2.8, w / h)); return cur != null && Math.abs(cur - r) < 0.02 ? cur : r; });
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [hidden, setHidden] = useState(true);

  return (
    <div className={`${s.plr} ${instant ? s.plrInstant : ""}`} role="dialog" aria-label="Player Settings">
      <div className={s.plrStage} ref={stageRef}>
        {ratio != null && (
          <div className={`${s.plrArena} ${hidden ? s.plrHidden : ""}`} style={{ "--ar": ratio } as React.CSSProperties}>
            <WormStage ratio={ratio} onFade={setHidden} player={{ name: me.name, colorId: me.colorId, flag: me.flag ?? null, headStyle: head ?? null, victoryAnim: victory ?? null }} />
          </div>
        )}
      </div>

      <div className={s.plrBar}>
        <button type="button" className={s.plrBack} onClick={onClose} aria-label="Back"><ChevL /></button>
        <h2>Player Settings</h2>
        {/* your profile needs an account: signed out, this is the way to sign in */}
        {signedIn
          ? <>
            <button type="button" className={s.plrOut} onClick={onSignOut}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" /></svg>
              Sign out
            </button>
            <button type="button" className={s.plrLink} onClick={onProfile}><Person />Profile</button>
          </>
          : !ON_PORTAL && <button type="button" className={`${s.signIn} ${s.plrSignIn}`} onClick={onLogin}>Sign in</button>}
        {onShop && (
          <button type="button" className="section-egg-pill" aria-label={eggs != null ? `${eggs} Golden Eggs. Open the shop` : "Open the shop"} onClick={() => onShop()}>
            <img src={eggIconUrl(44)} alt="" />
            {eggs != null ? <b>{eggs.toLocaleString()}</b> : null}
            <i aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg></i>
          </button>
        )}
      </div>

      <div className={s.plrFoot}>
        <div className={s.plrName}>
          {/* one field: its title sits inside it, over what you type */}
          <label className={s.plrField}>
            <small>Name</small>
            <input value={me.name} maxLength={16} onChange={(e) => roster.setName(0, e.target.value)} spellCheck={false} />
          </label>
        </div>
        <div className={s.plrWear}>
          {/* the colours open over the cards, nothing moves */}
          {colours && <div className={s.plrColours}><Swatches player={me} players={roster.players.slice(0, 1)} onPick={(c) => { roster.setColor(0, c); setColours(false); }} /></div>}
          <Equipment flag={me.flag} onFlag={(f) => roster.setFlag(0, f)} onShop={onShop && ((tab) => onShop(tab))} colour={{ color: (wormColors as Record<string, string>)[me.colorId], onClick: () => setColours((v) => !v) }} />
        </div>
      </div>
    </div>
  );
}
